import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiEye, FiCode, FiSmartphone } from 'react-icons/fi';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const PremiumSkills = () => {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);

  const skillCategories = [
    {
      title: 'AI & Machine Learning',
      icon: FiCpu,
      skills: [
        'TensorFlow',
        'PyTorch',
        'Keras',
        'scikit-learn',
        'Deep Learning',
        'Transformers',
        'Model Training',
        'Deployment',
      ],
    },
    {
      title: 'Computer Vision & NLP',
      icon: FiEye,
      skills: [
        'OpenCV',
        'YOLO',
        'Image Classification',
        'Object Detection',
        'NLTK',
        'spaCy',
        'NLP Pipelines',
        'Text Processing',
      ],
    },
    {
      title: 'Full Stack Development',
      icon: FiCode,
      skills: [
        'React.js',
        'Node.js',
        'JavaScript',
        'Python',
        'APIs & REST',
        'SQL & NoSQL',
        'Git & GitHub',
        'Docker',
      ],
    },
    {
      title: 'Mobile & Tools',
      icon: FiSmartphone,
      skills: [
        'Flutter',
        'Dart',
        'Cross-platform',
        'VS Code',
        'PyCharm',
        'Postman',
        'Streamlit',
        'Gradio',
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const categoryVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="skills" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background Elements */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute -bottom-40 -right-40 w-80 h-80 bg-accent-500 rounded-full blur-3xl opacity-10"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="Skills &" accent="Expertise" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Technologies and tools I work with regularly
          </p>
        </motion.div>

        {/* Skills Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {skillCategories.map((category, idx) => {
            const Icon = category.icon;
            return (
              <motion.div
                key={idx}
                variants={categoryVariants}
                onMouseEnter={() => setHoveredSkill(idx)}
                onMouseLeave={() => setHoveredSkill(null)}
                className="group cursor-pointer h-full"
                whileHover={{ y: -12 }}
              >
                <div
                  className="relative h-full rounded-2xl overflow-hidden p-6 transition-all duration-300"
                  style={{
                    perspective: '1000px',
                    transformStyle: 'preserve-3d',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `rotateX(${y * 8}deg) rotateY(${x * 8}deg) translateZ(15px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0) rotateY(0) translateZ(0)';
                  }}
                >
                  {/* Background Layers */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10 group-hover:from-primary-500/20 group-hover:to-accent-500/20 transition-all duration-300" />
                  <div className="absolute inset-0 bg-dark-800/40 backdrop-blur-sm group-hover:bg-dark-800/30 transition-all duration-300" />

                  {/* Content */}
                  <div className="relative h-full flex flex-col">
                    {/* Icon & Title */}
                    <div className="mb-6">
                      <div className="text-4xl text-primary-500 mb-3">
                        <Icon />
                      </div>
                      <h3 className="text-xl font-bold text-white group-hover:text-primary-500 transition-colors duration-300">
                        {category.title}
                      </h3>
                    </div>

                    {/* Skills List */}
                    <div className="flex-1">
                      <div className="grid grid-cols-2 gap-2">
                        {category.skills.map((skill, skillIdx) => (
                          <motion.div
                            key={skillIdx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{
                              opacity: hoveredSkill === idx ? 1 : 0.7,
                              scale: hoveredSkill === idx ? 1 : 0.95,
                            }}
                            transition={{ delay: skillIdx * 0.03 }}
                            className={`px-3 py-2 rounded-lg text-xs font-medium text-center ${
                              hoveredSkill === idx
                                ? 'bg-primary-500/30 text-primary-300 border border-primary-500/50'
                                : 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                            } transition-all duration-300`}
                          >
                            {skill}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Border Glow */}
                  <div className="absolute inset-0 rounded-2xl border border-primary-500/0 group-hover:border-primary-500/50 transition-all duration-300" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumSkills;
